<template>
  <section class="sync-workspace" v-loading="syncing" element-loading-text="同步中...">
    <div v-if="!isNacosPage" class="sync-card group-card">
      <div class="card-title">UFO 分组</div>
      <div class="group-segment">
        <button
          v-for="item in groupNodes"
          :key="item.groupKey"
          type="button"
          :class="['segment-item', { active: tabNodeGroup === item.groupKey }]"
          @click="handleSelectNodeGroup(item.groupKey)"
        >
          {{ item.groupKey }}
        </button>
        <span v-if="!groupNodes.length" class="empty-inline">暂无分组</span>
      </div>
    </div>

    <div v-if="!isNacosPage" class="sync-card module-card">
      <div class="card-title">功能模块</div>
      <el-tabs v-model="activeName" @tab-click="handleActiveNameClick" class="module-tabs">
        <el-tab-pane label="渠道参数" name="channel"></el-tab-pane>
        <el-tab-pane label="游戏参数" name="app"></el-tab-pane>
        <el-tab-pane label="节假日" name="holiday"></el-tab-pane>
        <el-tab-pane label="包证书" name="pkgConfig"></el-tab-pane>
        <el-tab-pane label="卸载菜单栏" name="uninstallMenu"></el-tab-pane>
        <el-tab-pane label="Visitor" name="visitor"></el-tab-pane>
      </el-tabs>
    </div>

    <div v-if="!isNacosPage" class="sync-card operation-card">
      <div class="card-title">渠道与操作</div>
      <div class="operation-row">
        <div class="selector-block">
          <span class="field-label">{{ selectorLabel }}</span>
          <el-select
            v-if="activeName === 'channel' || activeName === 'uninstallMenu'"
            v-model="channelId"
            placeholder="请选择渠道"
            @change="handleChannelChange"
            clearable
            filterable
            size="small"
            class="object-select"
          >
            <el-option v-for="item in channelOptions" :key="item.id" :label="item.id + ' - ' + item.name" :value="item.id"></el-option>
          </el-select>

          <el-select
            v-else-if="activeName === 'holiday'"
            v-model="holidayYear"
            placeholder="请选择年份"
            @change="handleHolidayYearChange"
            clearable
            filterable
            size="small"
            class="object-select"
          >
            <el-option v-for="item in holidayOptions" :key="item.id" :value="item.year" :label="item.year"></el-option>
          </el-select>

          <el-select
            v-else-if="activeName === 'pkgConfig'"
            v-model="pkgValue"
            placeholder="请选择包证书"
            @change="handleChannelChange"
            clearable
            filterable
            size="small"
            class="object-select"
          >
            <el-option v-for="item in pkgOptions" :key="item.id" :label="item.id + ' - ' + item.name" :value="item.id"></el-option>
          </el-select>

          <div v-else class="readonly-selector">{{ selectorReadonlyText }}</div>
        </div>

        <div class="action-block">
          <span class="field-label">操作</span>
          <div class="action-buttons">
            <el-button
              v-for="action in availableActions"
              :key="action.type"
              size="small"
              :type="getActionButtonType(action.type)"
              :plain="getActionButtonPlain(action.type)"
              :icon="action.icon"
              :disabled="action.disabled"
              @click="handleActionClick(action.type)"
            >
              {{ getActionButtonLabel(action.type) }}
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isNacosPage" class="sync-card nacos-card">
      <div class="nacos-panel">
        <div class="nacos-panel-header">
          <div>
            <div class="card-title">重载NACOS</div>
            <div class="nacos-selected-tip">已选择 {{ nacosConfigKeys.length }} / {{ nacosConfigOptions.length }} 个配置组</div>
          </div>
          <div class="nacos-action-bar">
            <el-button size="small" plain :icon="isAllNacosSelected ? 'el-icon-close' : 'el-icon-finished'" @click="handleToggleAllNacosConfig">{{ isAllNacosSelected ? '清空选择' : '全选配置' }}</el-button>
            <el-button size="small" type="success" icon="el-icon-refresh" :disabled="syncing || !nacosConfigKeys.length" @click="handleNacosSync">重载NACOS</el-button>
          </div>
        </div>

        <div class="nacos-grid-container">
          <div
            v-for="item in nacosConfigOptions"
            :key="item.key"
            :class="['nacos-config-card', { checked: nacosConfigKeys.includes(item.key) }, getNacosCardStatusClass(item)]"
            @click="toggleNacosConfigKey(item.key)"
          >
            <div class="card-checkbox-wrapper">
              <el-checkbox :value="nacosConfigKeys.includes(item.key)" @click.native.prevent.stop></el-checkbox>
            </div>
            <div class="card-main-content">
              <div class="card-icon-box">
                <i :class="getNacosConfigIcon(item.key)"></i>
              </div>
              <div class="card-text">
                <h4 class="card-title-text">{{ item.name }}</h4>
                <p class="card-desc">{{ getNacosConfigItemNames(item).join('、') }}</p>
              </div>
            </div>
            <div class="card-status-footer">
              <span>{{ getNacosConfigItemNames(item).length }} 个同步项</span>
              <span :class="['sync-badge', getNacosCardStatus(item)]">
                <i :class="getNacosSyncIcon(item)"></i>
                {{ getNacosSyncStatusText(item) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="sync-card result-card">
      <div class="result-card-header">
        <div class="card-title">{{ isNacosPage ? '重载结果' : '节点列表与服务结果' }}</div>
        <el-button size="small" icon="el-icon-refresh" plain :disabled="!canRefreshResult" @click="handleRefreshResult">刷新结果</el-button>
      </div>

      <div v-if="isNacosPage" class="nacos-result-list">
        <template v-if="nacosResult.length">
          <div class="nacos-result-row" v-for="item in nacosResult" :key="item.name">
            <span class="nacos-result-name">{{ item.name }}</span>
            <span :class="['status-pill', item.status ? 'success' : 'error']">
              <i :class="item.status ? 'el-icon-success' : 'el-icon-error'"></i>
              {{ item.status ? '成功' : '失败' }}
            </span>
            <code>{{ item.value }}</code>
          </div>
        </template>
        <div v-else class="empty-state">暂无 Nacos 重载结果，请选择配置后点击重载NACOS</div>
      </div>

      <div v-else class="result-layout">
        <div class="node-panel">
          <div class="panel-subhead">
            <span>当前列表（UFO 分组：{{ tabNodeGroup || '-' }}）</span>
          </div>
          <div class="node-table-head">
            <el-checkbox :indeterminate="isIndeterminate" v-model="checkAll" @change="handleCheckAllChange">全选</el-checkbox>
            <span>节点</span>
            <span>同步状态</span>
          </div>

          <div v-if="nodeOption.length" class="node-list">
            <div
              v-for="item in nodeOption"
              :key="item"
              :class="['node-row', { active: activeResultHost === item }]"
              @click="activeResultHost = item"
            >
              <el-checkbox
                :value="nodeList.includes(item)"
                @change="handleNodeCheckChange(item, $event)"
                @click.native.stop
              ></el-checkbox>
              <span class="node-host">{{ item }}</span>
              <span :class="['status-pill', getNodeStatus(item)]">
                <i :class="getNodeStatusIcon(item)"></i>
                {{ getNodeStatusText(item) }}
              </span>
            </div>
          </div>
          <div v-else class="empty-state">暂无节点，请切换 UFO 分组或稍后重试</div>
        </div>

        <div class="server-panel">
          <div class="result-toolbar">
            <div>
              <span class="result-label">服务结果详情（节点：{{ activeResultHost || '-' }}）</span>
              <span v-if="activeHostResult" :class="['result-state', activeHostResult.status ? 'success' : 'error']">
                {{ activeHostResult.status ? '成功' : '失败' }}
              </span>
            </div>
            <el-button size="mini" type="text" icon="el-icon-document-copy" :disabled="!activeResultText" @click="handleCopyText(activeResultText)">复制</el-button>
          </div>

          <div v-if="activeHostResult" class="code-shell">
            <pre class="json-code"><code>{{ activeResultText }}</code></pre>
          </div>
          <div v-else class="empty-result">
            <i class="el-icon-document"></i>
            <p>{{ nodeOption.length ? '请选择操作并查看服务端返回结果' : '暂无可查看节点' }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import api from '@/api/channel'

export default {
  name: 'index',
  created () {
    this.initializePageMode()
    this.fetchData()
    this.fetchHolidays()
  },
  computed: {
    isNacosPage () {
      return this.$route.path === '/nacos'
    },
    appId () {
      return this.$store.getters.appId
    },
    appInfo () {
      return this.$store.getters.appInfo || {}
    },
    appDisplayText () {
      const appName = this.appInfo.name || this.appInfo.appName || ''
      return appName ? `${this.appId} - ${appName}` : `${this.appId || '-'}`
    },
    selectorLabel () {
      const labels = {
        channel: '渠道选择',
        app: '当前游戏',
        holiday: '节假日年份',
        pkgConfig: '包证书',
        uninstallMenu: '渠道选择',
        visitor: 'Visitor'
      }
      return labels[this.activeName] || '操作对象'
    },
    selectorReadonlyText () {
      if (this.activeName === 'app') return this.appDisplayText
      if (this.activeName === 'visitor') return 'Visitor 全量同步'
      return '-'
    },
    activeResultText () {
      if (!this.activeHostResult) return ''
      return this.formatTerminalValue(this.activeHostResult.value)
    },
    canRefreshResult () {
      if (this.syncing) return false
      if (this.isNacosPage) return this.nacosConfigKeys.length > 0
      if (this.activeName === 'visitor') return this.nodeList.length > 0
      return this.hasAction('get') && !this.isActionDisabled('get') && this.nodeList.length > 0
    },
    isAllNacosSelected () {
      return this.nacosConfigOptions.length > 0 && this.nacosConfigKeys.length === this.nacosConfigOptions.length
    },
    activeConfigLabel () {
      const labels = {
        channel: '渠道参数',
        app: '游戏参数',
        holiday: '节假日',
        pkgConfig: '包证书',
        uninstallMenu: '卸载菜单栏',
        visitor: 'Visitor',
        nacosConfig: 'Nacos配置'
      }
      return labels[this.activeName] || '参数配置'
    },
    canSyncNacos () {
      return this.activeName === 'nacosConfig'
    },
    currentNacosReloadItems () {
      if (this.activeName !== 'nacosConfig') return []
      return this.selectedNacosConfigOptions.reduce((items, option) => {
        return items.concat(option.items)
      }, [])
    },
    selectedNacosConfigOptions () {
      return this.nacosConfigOptions.filter(item => this.nacosConfigKeys.includes(item.key))
    },
    hasBusinessActions () {
      return ['post', 'put', 'delete', 'get', 'visitor_pull'].some(action => this.hasAction(action))
    },
    nacosStatusClass () {
      if (!this.nacosResult.length) return 'pending'
      return this.nacosResult.some(item => !item.status) ? 'error' : 'success'
    },
    nacosStatusText () {
      if (!this.nacosResult.length) return this.canSyncNacos ? '待同步' : '不需要'
      return this.nacosResult.some(item => !item.status) ? '失败' : '成功'
    },
    parameterSuccessCount () {
      return this.result.filter(item => item.status).length
    },
    parameterFailCount () {
      return this.result.filter(item => !item.status).length
    },
    activeHostResult () {
      if (!this.result || !this.result.length) return null
      if (!this.activeResultHost) return this.result[0] || null
      return this.result.find(r => r.host === this.activeResultHost) || this.result[0] || null
    },
    isResultJson () {
      if (!this.activeHostResult || !this.activeHostResult.value) return false

      if (typeof this.activeHostResult.value === 'object') return true

      const valStr = String(this.activeHostResult.value).trim()
      if (valStr.startsWith('{') || valStr.startsWith('[')) {
        try {
          JSON.parse(valStr)
          return true
        } catch (e) {
          return false
        }
      }
      return false
    },
    showTerminalResult () {
      return Boolean(this.visible && this.result.length && !this.isResultJson)
    },
    tableDataFromResult () {
      if (!this.activeHostResult) return []

      let parsedVal = null
      if (typeof this.activeHostResult.value === 'object') {
        parsedVal = this.activeHostResult.value
      } else {
        try {
          parsedVal = JSON.parse(this.activeHostResult.value)
        } catch (e) {
          parsedVal = null
        }
      }

      if (!parsedVal) return []

      const list = []

      if (Array.isArray(parsedVal)) {
        parsedVal.forEach((item, index) => {
          list.push({
            id: index,
            key: item.key || item.paramKey || item.id || String(index),
            value: typeof item.value === 'object' ? JSON.stringify(item.value) : String(item.value || '')
          })
        })
      } else {
        Object.keys(parsedVal).forEach((key, index) => {
          const val = parsedVal[key]
          list.push({
            id: index,
            key: key,
            value: typeof val === 'object' ? JSON.stringify(val) : String(val)
          })
        })
      }

      return list
    },
    filteredTableData () {
      const data = this.tableDataFromResult
      const query = this.searchQuery ? this.searchQuery.trim().toLowerCase() : ''
      if (!query) return data

      return data.filter(item => {
        return (
          String(item.key).toLowerCase().includes(query) ||
          String(item.value).toLowerCase().includes(query)
        )
      })
    },

    // === 自适应生成动作 Hero 卡片 ===
    availableActions () {
      const activeName = this.activeName
      const actions = []

      if (activeName === 'visitor') {
        actions.push({
          type: 'visitor_pull',
          name: '同步更新数据',
          desc: '从访客节点同步并重新加载最新的全部参数数据',
          icon: 'el-icon-refresh',
          disabled: this.syncing
        })
        return actions
      }

      if (this.hasAction('get')) {
        actions.push({
          type: 'get',
          name: '获取并查看参数',
          desc: '从选中的主机节点中抓取已下发的配置参数详情',
          icon: 'el-icon-view',
          disabled: this.isActionDisabled('get') || this.syncing
        })
      }
      if (this.hasAction('post')) {
        actions.push({
          type: 'post',
          name: '添加配置参数',
          desc: '向选中的多台服务器节点快速分发新参数项',
          icon: 'el-icon-plus',
          disabled: this.isActionDisabled('post') || this.syncing
        })
      }
      if (this.hasAction('put')) {
        actions.push({
          type: 'put',
          name: '修改下发参数',
          desc: '覆盖并同步更新当前节点中已修改的参数键值',
          icon: 'el-icon-edit',
          disabled: this.isActionDisabled('put') || this.syncing
        })
      }
      if (this.hasAction('delete')) {
        actions.push({
          type: 'delete',
          name: '删除下发参数',
          desc: '从多台主机节点中抹除当前类型的生效配置参数',
          icon: 'el-icon-delete',
          disabled: this.isActionDisabled('delete') || this.syncing
        })
      }

      return actions
    }
  },
  watch: {
    '$route.path' () {
      this.initializePageMode()
      this.clearSyncResults()
    },
    appId (val, oldVal) {
      if (val === oldVal) {
        return
      }
      this.fetchData()
    },
    result (val) {
      if (val && val.length > 0) {
        if (!this.activeResultHost) {
          this.activeResultHost = val[0].host
        }
      } else {
        this.activeResultHost = this.nodeOption[0] || ''
      }
    }
  },
  data () {
    return {
      tableData: [],
      result: [],
      argument: '',
      channelOptions: [],
      channelId: '',
      tabNodeGroup: '',
      groupNodes: [],
      nodeOption: [],
      nodeList: [],
      checkAll: true,
      isIndeterminate: false,
      visible: false,
      activeName: 'channel',
      holidayOptions: [],
      holidayYear: '',
      ufwType: '1',
      pkgValue: '',
      pkgOptions: [],
      nacosResult: [],
      syncing: false,
      lastSyncTime: '',
      nacosConfigKeys: [],
      nacosConfigOptions: [
        { key: 'channel', name: '渠道配置', items: [{ name: '渠道配置', url: '/channel/admin/cache/reload/channel' }] },
        { key: 'game', name: '游戏配置', items: [{ name: '游戏配置', url: '/channel/admin/cache/reload/game' }] },
        { key: 'mchGroup', name: '商户组管理', items: [{ name: '商户组管理', url: '/channel/admin/cache/reload/mch/group' }] },
        { key: 'mchWx', name: '微信商户', items: [{ name: '微信商户', url: '/channel/admin/cache/reload/mch/wx' }] },
        { key: 'mchAli', name: '支付宝商户', items: [{ name: '支付宝商户', url: '/channel/admin/cache/reload/mch/ali' }] },
        { key: 'mchApple', name: 'Apple应用', items: [{ name: 'Apple应用', url: '/channel/admin/cache/reload/mch/apple' }] },
        {
          key: 'xsArgsGame',
          name: 'XS游戏',
          items: [
            { name: 'XS登录参数', url: '/channel/admin/cache/reload/xsArgs/login' },
            { name: 'XS支付参数', url: '/channel/admin/cache/reload/xsArgs/pay' }
          ]
        },
        {
          key: 'xsArgsChannel',
          name: 'XS渠道',
          items: [
            { name: 'XS登录参数', url: '/channel/admin/cache/reload/xsArgs/login' },
            { name: '渠道配置', url: '/channel/admin/cache/reload/channel' },
            { name: 'XS支付参数', url: '/channel/admin/cache/reload/xsArgs/pay' }
          ]
        },
        { key: 'telecomAuth', name: '电信认证', items: [{ name: '电信认证', url: '/channel/admin/cache/reload/telecom/auth' }] },
        { key: 'extGameConfig', name: 'Ext游戏', items: [{ name: 'Ext游戏配置', url: '/channel/admin/cache/reload/ext/config/game' }] },
        { key: 'extChannelConfig', name: 'Ext渠道', items: [{ name: 'Ext渠道配置', url: '/channel/admin/cache/reload/ext/config/channel' }] }
      ],

      searchQuery: '',
      activeResultHost: ''
    }
  },
  mounted () {
    api.getPkgList({ size: 1000 }).then(res => {
      this.pkgOptions = res._embedded.pkgConfigs.reduce((pre, cur) => {
        const arr = {
          id: cur.pkgName,
          name: cur.name
        }
        return pre ? [...pre, arr] : [arr]
      }, [])
    })
  },
  methods: {
    initializePageMode () {
      this.activeName = this.$route.path === '/nacos' ? 'nacosConfig' : 'channel'
    },
    formatNow () {
      const date = new Date()
      return date.toLocaleString('zh-CN', { hour12: false })
    },
    handleSelectNodeGroup (groupKey) {
      if (this.tabNodeGroup === groupKey) return
      this.tabNodeGroup = groupKey
      this.handleTabNodeGroupClick()
    },
    handleNodeCheckChange (node, checked) {
      const next = checked
        ? Array.from(new Set([...this.nodeList, node]))
        : this.nodeList.filter(item => item !== node)
      this.handleCheckedCitiesChange(next)
    },
    handleRefreshResult () {
      if (!this.canRefreshResult) {
        this.$message.info('请先选择必要的同步对象和节点')
        return
      }
      if (this.isNacosPage) {
        this.handleNacosSync()
        return
      }
      if (this.activeName === 'visitor') {
        this.handleActionClick('visitor_pull')
        return
      }
      if (this.hasAction('get')) {
        this.handleActionClick('get')
      } else {
        this.$message.info('当前模块暂无查看操作')
      }
    },
    getActionButtonLabel (type) {
      const labels = {
        get: '查看',
        post: '添加',
        put: '修改',
        delete: '删除',
        visitor_pull: '查看'
      }
      return labels[type] || type
    },
    getActionButtonType (type) {
      const types = {
        get: 'primary',
        post: 'success',
        put: 'primary',
        delete: 'danger',
        visitor_pull: 'primary'
      }
      return types[type] || 'default'
    },
    getActionButtonPlain (type) {
      return false
    },
    getNodeResult (node) {
      return this.result.find(item => item.host === node)
    },
    getNodeStatus (node) {
      if (this.syncing && this.nodeList.includes(node)) return 'syncing'
      const result = this.getNodeResult(node)
      if (!result) return 'pending'
      return result.status ? 'success' : 'error'
    },
    getNodeStatusText (node) {
      const texts = {
        pending: '未同步',
        syncing: '同步中',
        success: '成功',
        error: '失败'
      }
      return texts[this.getNodeStatus(node)]
    },
    getNodeStatusIcon (node) {
      const icons = {
        pending: 'el-icon-time',
        syncing: 'el-icon-loading',
        success: 'el-icon-success',
        error: 'el-icon-error'
      }
      return icons[this.getNodeStatus(node)]
    },
    clearSyncResults () {
      this.result = []
      this.nacosResult = []
      this.visible = false
      this.searchQuery = ''
      this.activeResultHost = this.nodeList[0] || ''
    },
    appendNodeResult (host, value, status) {
      const item = { host, value, status }
      this.result.push(item)
      if (!this.activeResultHost || !this.result.some(result => result.host === this.activeResultHost)) {
        this.activeResultHost = host
      }
      this.visible = true
    },
    getCurrentContextText () {
      if (this.activeName === 'nacosConfig') {
        return this.selectedNacosConfigOptions.map(item => item.name).join('、')
      }
      if (this.activeName === 'channel' || this.activeName === 'uninstallMenu') {
        const channel = this.channelOptions.find(item => item.id === this.channelId)
        return channel ? `${channel.id} - ${channel.name}` : this.channelId
      }
      if (this.activeName === 'holiday') return this.holidayYear
      if (this.activeName === 'pkgConfig') return this.pkgValue
      if (this.activeName === 'app') return this.appId
      return this.activeConfigLabel
    },
    buildNacosMessage () {
      const reloadNames = this.currentNacosReloadItems.map(item => item.name).join('..')
      return `
        <div style="text-align:left;line-height:1.8;">
          <div><b>配置类型：</b>${this.activeConfigLabel}</div>
          <div><b>业务对象：</b>${this.getCurrentContextText() || '-'}</div>
          <div><b>将执行：</b>${reloadNames || '同步 Nacos 配置'}</div>
        </div>
      `
    },
    confirmNacosSync () {
      return this.$confirm(this.buildNacosMessage(), '同步 Nacos 确认', {
        confirmButtonText: '同步 Nacos',
        cancelButtonText: '取消',
        type: 'warning',
        dangerouslyUseHTMLString: true
      })
    },
    runNacosSync () {
      const reloadItems = this.currentNacosReloadItems
      if (!reloadItems.length) return Promise.resolve()
      this.nacosResult = reloadItems.map(item => ({ name: item.name, value: '同步中...', status: true }))
      return Promise.all(reloadItems.map(item => {
        return api.cacheReload(item.url, { appId: this.appId }).then(() => {
          return { name: item.name, value: '同步成功', status: true }
        }).catch(error => {
          const message = error && error.data && error.data.msg ? error.data.msg : '同步失败'
          return { name: item.name, value: message, status: false }
        })
      })).then(result => {
        this.nacosResult = result
      })
    },
    handleNacosSync () {
      if (!this.canSyncNacos) {
        this.$message.info('当前配置类型未发现现有 Nacos 重载接口')
        return
      }
      if (!this.nacosConfigKeys.length) {
        this.$message.info('请选择需要同步的 Nacos 配置')
        return
      }
      this.confirmNacosSync().then(async () => {
        this.syncing = true
        this.nacosResult = []
        await this.runNacosSync()
        this.lastSyncTime = this.formatNow()
        if (this.nacosStatusClass === 'error') {
          this.$message.warning('Nacos 同步失败，请查看结果')
        } else {
          this.$message.success('Nacos 同步完成')
        }
      }).catch(() => {
        this.$message.info('已取消同步')
      }).finally(() => {
        this.syncing = false
      })
    },
    handleNacosConfigChange () {
      this.nacosResult = []
      this.lastSyncTime = ''
    },
    getNacosConfigItemNames (option) {
      return option.items.map(item => item.name)
    },
    fetchData () {
      this.groupNodes = []
      this.clearNodeResult()
      api.getNodeList({ appId: this.appId, format: 'group' }).then(res => {
        if (res.length <= 0) {
          return
        }
        this.groupNodes = res
        const gn = res.find(d => d.selected) || res[0]
        this.tabNodeGroup = gn.groupKey
        this.nodeOption = gn.nodes
        this.activeResultHost = this.nodeOption[0] || ''
        this.handleCheckAllChange(true)
      })
      this.channelOptions = []
      api.getChannelData(this.appId).then(res => {
        this.channelOptions = Object.assign([], res)
      })
    },

    handleTabNodeGroupClick () {
      this.clearNodeResult()
      const gn = this.groupNodes.find(d => d.groupKey === this.tabNodeGroup) || this.groupNodes[0]
      this.nodeOption = gn.nodes
      this.activeResultHost = this.nodeOption[0] || ''
      this.handleCheckAllChange(true)
    },
    handleCheckAllChange (val) {
      this.nodeList = val ? this.nodeOption : []
      this.isIndeterminate = false
      this.activeResultHost = this.nodeList[0] || ''
      this.result = []
      this.visible = false
    },
    handleCheckedCitiesChange (value) {
      this.nodeList = value
      const checkedCount = value.length
      this.checkAll = checkedCount === this.nodeOption.length
      this.isIndeterminate = checkedCount > 0 && checkedCount < this.nodeOption.length
      if (!value.includes(this.activeResultHost)) {
        this.activeResultHost = value[0] || ''
      }
      this.result = []
      this.visible = false
    },
    handleChannelNode (type) {
      this.result = []
      if (!this.nodeList.length) {
        return this.$message.info('请选择节点')
      }
      if (type !== 'delete') {
        this.handleFetchData(type)
      } else {
        this.$confirm('此操作将全部删除, 是否继续?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.handleFetchData(type)
        }).catch(() => {
          this.$message({
            type: 'info',
            message: '已取消删除'
          })
        })
      }
    },
    handleFetchData (type) {
      for (const item of this.nodeList) {
        api.handleArgsByType(this.channelId, type, { host: item }).then(res => {
          if (res.code === 200) {
            if (type === 'delete') {
              this.$message.success('成功')
            }
            this.appendNodeResult(item, res.msg, true)
          } else {
            this.appendNodeResult(item, res.msg, false)
          }
        })
      }
    },
    handleGetChannelArgs () {
      if (!this.nodeList.length) {
        return this.$message.info('请选择节点')
      }
      this.result = []
      for (const item of this.nodeList) {
        api.getChannelArgs(this.channelId, { host: item }).then(res => {
          this.appendNodeResult(item, res, true)
        })
      }
    },
    handleGetGameNode () {
      if (!this.nodeList.length) {
        return this.$message.info('请选择节点')
      }
      this.result = []
      for (const item of this.nodeList) {
        api.getAppArgs(this.appId, { host: item }).then(res => {
          this.appendNodeResult(item, res, true)
        })
      }
    },
    handleChangeGameNode () {
      if (!this.nodeList.length) {
        return this.$message.info('请选择节点')
      }
      this.result = []
      for (const item of this.nodeList) {
        api.changeAppArgs(this.appId, { host: item }).then(res => {
          if (res.code === 200) {
            this.appendNodeResult(item, res.msg, true)
          } else {
            this.appendNodeResult(item, res.msg, false)
          }
        })
      }
    },
    handleHint (node) {
      if (!this.result.length) {
        return false
      }
      for (const item of this.result) {
        if (item.host === node && !item.status) {
          return false
        }
      }
      return true
    },
    handleChannelChange () {
      this.clearNodeResult()
    },
    clearNodeResult () {
      this.result = []
      this.nodeList = []
      this.isIndeterminate = false
      for (const item of this.nodeOption) {
        this.nodeList.push(item)
      }
      this.checkAll = true
      this.visible = false
      this.searchQuery = ''
    },
    clearResult () {
      this.result = []
      this.visible = false
      this.activeResultHost = this.nodeList[0] || ''
    },
    fetchHolidays () {
      api.getHolidays().then(res => {
        this.holidayOptions = Object.assign([], res)
      })
    },
    handleHolidayYearChange () {
      this.clearNodeResult()
    },
    handleNodeHolidayOperate (type) {
      this.clearResult()
      for (const host of this.nodeList) {
        api.handleNodeHoliday(type, this.holidayYear, { host: host }).then(res => {
          if (res.code === 200) {
            this.appendNodeResult(host, res, true)
          } else {
            this.appendNodeResult(host, res.msg, false)
          }
        })
      }
    },
    handleUfwRules () {
      this.clearResult()
      if (!this.ufwType) {
        this.$message.info('请选择UFW类型')
        return
      }
      for (const host of this.nodeList) {
        const apiFunc = this.ufwType === '1' ? api.ufwBlacklists : api.ufwWhitelists
        apiFunc({ host: host }).then(res => {
          if (res.code === 200) {
            this.appendNodeResult(host, res.data || [], true)
          } else {
            this.appendNodeResult(host, res.msg, false)
          }
        })
      }
    },
    handlePkgConfig (type) {
      if (!this.nodeList.length) {
        return this.$message.error('请选择节点')
      }
      switch (type) {
        case 'delete':
          this.$confirm('确定是否删除?', '', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            this.handlePkgFetchData(type)
          }).catch(() => {
            this.$message({
              type: 'info',
              message: '已取消删除'
            })
          })
          break
        default:
          this.handlePkgFetchData(type)
          break
      }
    },
    handlePkgFetchData (type) {
      if (this.result.length) this.result = []
      for (const item of this.nodeList) {
        api.handlePkgRequest(this.pkgValue, type, { host: item }).then(res => {
          this.visible = true
          if (res.code === 200) {
            if (type === 'delete') {
              this.$message.success('成功')
            }
            this.appendNodeResult(item, res.data, true)
          } else {
            this.appendNodeResult(item, res.msg, false)
          }
        })
      }
    },
    handleUninstallMenuConfig (type) {
      if (!this.nodeList.length) {
        return this.$message.error('请选择节点')
      }
      switch (type) {
        case 'delete':
          this.$confirm('确定是否删除?', '', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            this.handleUninstallMenuFetchData(type)
          }).catch(() => {
            this.$message({
              type: 'info',
              message: '已取消删除'
            })
          })
          break
        default:
          this.handleUninstallMenuFetchData(type)
          break
      }
    },
    handleUninstallMenuFetchData (type) {
      if (this.result.length) this.result = []
      for (const item of this.nodeList) {
        api.handleUninstallMenuRequest(this.channelId, type, { host: item }).then(res => {
          this.visible = true
          if (res.code === 200) {
            if (type === 'delete') {
              this.$message.success('成功')
            }
            this.appendNodeResult(item, res.data, true)
          } else {
            this.appendNodeResult(item, res.msg, false)
          }
        })
      }
    },
    handleVisitorPull () {
      if (!this.nodeList.length) {
        return this.$message.info('请选择节点')
      }
      this.result = []
      for (const item of this.nodeList) {
        api.visitorPull({ host: item }).then(res => {
          this.appendNodeResult(item, res, true)
        })
      }
    },
    handleActiveNameClick () {
      this.clearSyncResults()
    },
    handleCopyText (text) {
      const input = document.createElement('textarea')
      input.value = text
      document.body.appendChild(input)
      input.select()
      try {
        document.execCommand('copy')
        this.$message.success('已复制到剪贴板')
      } catch (err) {
        this.$message.error('复制失败，请手动复制')
      }
      document.body.removeChild(input)
    },
    formatTerminalValue (val) {
      if (typeof val === 'object') {
        return JSON.stringify(val, null, 2)
      }
      try {
        const valStr = String(val).trim()
        if (valStr.startsWith('{') || valStr.startsWith('[')) {
          const parsed = JSON.parse(valStr)
          return JSON.stringify(parsed, null, 2)
        }
      } catch (e) {}
      return val
    },
    handleActionClick (actionType) {
      if (actionType === 'get') {
        if (this.activeName === 'channel') this.handleGetChannelArgs()
        else if (this.activeName === 'app') this.handleGetGameNode()
        else if (this.activeName === 'holiday') this.handleNodeHolidayOperate('get')
        else if (this.activeName === 'pkgConfig') this.handlePkgConfig('get')
        else if (this.activeName === 'uninstallMenu') this.handleUninstallMenuConfig('get')
      } else if (actionType === 'post') {
        if (this.activeName === 'channel') this.handleChannelNode('post')
        else if (this.activeName === 'pkgConfig') this.handlePkgConfig('post')
        else if (this.activeName === 'uninstallMenu') this.handleUninstallMenuConfig('post')
      } else if (actionType === 'put') {
        if (this.activeName === 'channel') this.handleChannelNode('put')
        else if (this.activeName === 'app') this.handleChangeGameNode()
        else if (this.activeName === 'holiday') this.handleNodeHolidayOperate('put')
        else if (this.activeName === 'pkgConfig') this.handlePkgConfig('put')
        else if (this.activeName === 'uninstallMenu') this.handleUninstallMenuConfig('put')
      } else if (actionType === 'delete') {
        if (this.activeName === 'channel') this.handleChannelNode('delete')
        else if (this.activeName === 'holiday') this.handleNodeHolidayOperate('delete')
        else if (this.activeName === 'pkgConfig') this.handlePkgConfig('delete')
        else if (this.activeName === 'uninstallMenu') this.handleUninstallMenuConfig('delete')
      } else if (actionType === 'visitor_pull') {
        this.handleVisitorPull()
      }
    },
    hasAction (actionType) {
      const activeName = this.activeName
      if (activeName === 'visitor') {
        return actionType === 'visitor_pull'
      }
      if (actionType === 'visitor_pull') return false

      if (activeName === 'channel' || activeName === 'pkgConfig' || activeName === 'uninstallMenu') {
        return ['post', 'put', 'delete', 'get'].includes(actionType)
      }
      if (activeName === 'app') {
        return ['put', 'get'].includes(actionType)
      }
      if (activeName === 'holiday') {
        return ['put', 'delete', 'get'].includes(actionType)
      }
      return false
    },
    isActionDisabled (actionType) {
      const activeName = this.activeName
      if (actionType === 'get' && activeName === 'app') {
        return false
      }
      if (activeName === 'channel' || activeName === 'uninstallMenu') {
        return !this.channelId
      }
      if (activeName === 'holiday') {
        return !this.holidayYear
      }
      if (activeName === 'pkgConfig') {
        return !this.pkgValue
      }
      return false
    },
    getTipsText () {
      const activeName = this.activeName
      if (activeName === 'channel') {
        return '渠道参数将应用于所选渠道的所有相关游戏，请谨慎修改。'
      }
      if (activeName === 'app') {
        return '游戏参数将同步修改到游戏在各个节点上的基础配置。'
      }
      if (activeName === 'holiday') {
        return '修改节假日参数将同步至所选范围内的节点主机，用以影响服务状态。'
      }
      if (activeName === 'pkgConfig') {
        return '证书包名对应的包证书配置会直接分发到对应主机，请核对后修改。'
      }
      if (activeName === 'uninstallMenu') {
        return '卸载菜单栏配置属于渠道的专有服务，仅对选中渠道有效。'
      }
      if (activeName === 'nacosConfig') {
        return '请选择需要发布到 Nacos 的配置项，可一次选择多个配置并查看逐项同步结果。'
      }
      return '此操作涉及多台主机的数据同步，操作执行后将在各主机生效。'
    },
    handleNacosSelectAll () {
      this.nacosConfigKeys = this.nacosConfigOptions.map(item => item.key)
      this.nacosResult = []
      this.lastSyncTime = ''
    },
    handleNacosSelectNone () {
      this.nacosConfigKeys = []
      this.nacosResult = []
      this.lastSyncTime = ''
    },
    handleToggleAllNacosConfig () {
      if (this.isAllNacosSelected) {
        this.handleNacosSelectNone()
      } else {
        this.handleNacosSelectAll()
      }
    },
    toggleNacosConfigKey (key) {
      const idx = this.nacosConfigKeys.indexOf(key)
      if (idx > -1) {
        this.nacosConfigKeys.splice(idx, 1)
      } else {
        this.nacosConfigKeys.push(key)
      }
      this.nacosResult = []
      this.lastSyncTime = ''
    },
    getNacosConfigIcon (key) {
      const icons = {
        channel: 'el-icon-connection',
        game: 'el-icon-stopwatch',
        mchGroup: 'el-icon-folder-opened',
        mchWx: 'el-icon-chat-dot-round',
        mchAli: 'el-icon-wallet',
        mchApple: 'el-icon-apple',
        xsArgsGame: 'el-icon-medal',
        xsArgsChannel: 'el-icon-link',
        telecomAuth: 'el-icon-phone',
        extGameConfig: 'el-icon-cpu',
        extChannelConfig: 'el-icon-set-up'
      }
      return icons[key] || 'el-icon-document'
    },
    getNacosCardStatus (item) {
      if (!this.nacosResult.length) return 'pending'
      const itemNames = this.getNacosConfigItemNames(item)
      const matchResults = this.nacosResult.filter(r => itemNames.includes(r.name))
      if (!matchResults.length) return 'pending'
      if (matchResults.some(r => r.value === '同步中...')) return 'syncing'
      if (matchResults.some(r => !r.status)) return 'error'
      return 'success'
    },
    getNacosCardStatusClass (item) {
      const status = this.getNacosCardStatus(item)
      return `nacos-status-${status}`
    },
    getNacosSyncIcon (item) {
      const status = this.getNacosCardStatus(item)
      const icons = {
        pending: 'el-icon-time',
        syncing: 'el-icon-loading',
        success: 'el-icon-success',
        error: 'el-icon-error'
      }
      return icons[status]
    },
    getNacosSyncStatusText (item) {
      const status = this.getNacosCardStatus(item)
      const texts = {
        pending: '待同步',
        syncing: '同步中...',
        success: '同步成功',
        error: '同步失败'
      }
      return texts[status]
    }
  }
}
</script>

<style scoped>
.sync-workspace {
  min-height: calc(100vh - 56px);
  padding: 16px 24px 24px;
  background: #f8fafc;
  color: #1e293b;
  box-sizing: border-box;
  overflow-x: hidden;
}

.page-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 14px;
}

.breadcrumb-line {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
  font-size: 12px;
  line-height: 18px;
  margin-bottom: 6px;
}

.breadcrumb-line strong {
  color: #334155;
  font-weight: 600;
}

.page-heading h1 {
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  color: #0f172a;
}

.sync-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
  padding: 18px 20px;
  margin-bottom: 12px;
  box-sizing: border-box;
}

.sync-card > .card-title,
.result-card-header > .card-title {
  flex: 0 0 170px;
  color: #0f172a;
  font-size: 16px;
  font-weight: 700;
  line-height: 26px;
}

.group-card,
.module-card {
  display: flex;
  align-items: center;
  gap: 10px;
}

.group-segment {
  display: inline-flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  background: #f8fafc;
}

.segment-item {
  min-width: 104px;
  height: 32px;
  border: 0;
  border-right: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.segment-item:last-child {
  border-right: 0;
}

.segment-item.active {
  color: #2563eb;
  background: #eff6ff;
  box-shadow: inset 0 0 0 1px #2563eb;
}

.empty-inline {
  padding: 0 12px;
  color: #94a3b8;
  font-size: 13px;
}

.module-card {
  padding-bottom: 12px;
}

.module-tabs {
  flex: 1;
  min-width: 0;
  padding: 2px 0 0;
}

.module-tabs :deep(.el-tabs__header) {
  margin: 0;
}

.module-tabs :deep(.el-tabs__nav-wrap::after) {
  height: 1px;
  background: #f1f5f9;
}

.module-tabs :deep(.el-tabs__item) {
  height: 38px;
  line-height: 38px;
  color: #475569;
  font-size: 14px;
  font-weight: 700;
  padding: 0 26px !important;
  border-radius: 6px 6px 0 0;
  transition: color 0.18s ease, background-color 0.18s ease;
}

.module-tabs :deep(.el-tabs__item:first-child),
.module-tabs :deep(.el-tabs__item:nth-child(2)),
.module-tabs :deep(.el-tabs__item:last-child) {
  padding-left: 26px !important;
  padding-right: 26px !important;
}

.module-tabs :deep(.el-tabs__item:hover) {
  color: #2563eb;
  background: #f8fafc;
}

.module-tabs :deep(.el-tabs__item.is-active) {
  color: #2563eb;
  background: #eff6ff;
}

.module-tabs :deep(.el-tabs__active-bar) {
  height: 3px;
  background: #2563eb;
  border-radius: 999px;
}

.operation-card {
  padding-bottom: 16px;
}

.operation-row {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(360px, 1.2fr);
  gap: 28px;
  align-items: end;
}

.selector-block,
.action-block {
  min-width: 0;
}

.field-label {
  display: block;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
}

.object-select {
  width: 100%;
  max-width: 360px;
}

.readonly-selector {
  width: 100%;
  max-width: 360px;
  height: 32px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 13px;
  box-sizing: border-box;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.action-buttons :deep(.el-button) {
  min-width: 76px;
  border-radius: 6px;
  font-weight: 600;
}

.result-card {
  margin-bottom: 0;
}

.result-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.result-card-header .card-title {
  flex: none;
}

.result-layout {
  display: grid;
  grid-template-columns: minmax(320px, 0.72fr) minmax(420px, 1fr);
  gap: 16px;
  min-height: 380px;
}

.nacos-result-list {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.nacos-result-row {
  display: grid;
  grid-template-columns: minmax(160px, 220px) 90px minmax(220px, 1fr);
  align-items: center;
  gap: 12px;
  min-height: 48px;
  padding: 0 14px;
  border-bottom: 1px solid #f1f5f9;
}

.nacos-result-row:last-child {
  border-bottom: 0;
}

.nacos-result-name {
  color: #334155;
  font-size: 13px;
  font-weight: 700;
}

.nacos-result-row code {
  min-width: 0;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.node-panel,
.server-panel {
  min-width: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.panel-subhead,
.result-toolbar {
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 14px;
  border-bottom: 1px solid #e2e8f0;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  background: #ffffff;
}

.node-table-head {
  display: grid;
  grid-template-columns: 80px minmax(120px, 1fr) 98px;
  align-items: center;
  min-height: 40px;
  padding: 0 12px;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.node-list {
  max-height: 420px;
  overflow-y: auto;
}

.node-row {
  display: grid;
  grid-template-columns: 80px minmax(120px, 1fr) 98px;
  align-items: center;
  min-height: 46px;
  padding: 0 12px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.18s ease, box-shadow 0.18s ease;
}

.node-row:hover {
  background: #f8fafc;
}

.node-row.active {
  background: #eff6ff;
  box-shadow: inset 3px 0 0 #2563eb;
}

.node-host {
  color: #2563eb;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-pill {
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 56px;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.status-pill.pending {
  color: #64748b;
  background: #f1f5f9;
}

.status-pill.syncing {
  color: #d97706;
  background: #fffbeb;
}

.status-pill.success {
  color: #16a34a;
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
}

.status-pill.error {
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.result-label {
  color: #475569;
}

.result-state {
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.result-state.success {
  color: #16a34a;
  background: #ecfdf5;
}

.result-state.error {
  color: #dc2626;
  background: #fef2f2;
}

.code-shell {
  height: 420px;
  overflow: auto;
  background: #ffffff;
}

.json-code {
  margin: 0;
  min-height: 100%;
  padding: 14px 16px;
  color: #b91c1c;
  background: #ffffff;
  font-size: 12.5px;
  line-height: 1.65;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  white-space: pre;
}

.empty-state,
.empty-result {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
  padding: 24px;
}

.empty-result {
  height: 420px;
  flex-direction: column;
  gap: 10px;
}

.empty-result i {
  font-size: 28px;
  color: #cbd5e1;
}

.empty-result p {
  margin: 0;
}

@media (max-width: 1180px) {
  .operation-row,
  .result-layout {
    grid-template-columns: 1fr;
  }

  .object-select,
  .readonly-selector {
    max-width: none;
  }
}

@media (max-width: 768px) {
  .sync-workspace {
    padding: 12px;
  }

  .page-heading,
  .group-card,
  .module-card {
    align-items: stretch;
    flex-direction: column;
  }

  .card-title {
    flex: none;
  }

  .group-segment {
    width: 100%;
  }

  .segment-item {
    flex: 1;
    min-width: 0;
  }

  .module-tabs :deep(.el-tabs__item) {
    padding: 0 14px;
  }

  .node-table-head,
  .node-row {
    grid-template-columns: 52px minmax(120px, 1fr) 86px;
  }
}

.left-stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 节点选择大卡片 */
.node-card {
  background: #ffffff;
  border: 1px solid #dfe7f3;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
  padding: 14px 20px;
}

.node-tabs :deep(.el-tabs__nav-wrap::after) {
  height: 1px;
  background-color: #f1f5f9;
}

.node-tabs :deep(.el-tabs__item) {
  font-size: 15px;
  font-weight: 600;
  color: #64748b;
  height: 40px;
  line-height: 40px;
  transition: all 0.2s ease;
}

.node-tabs :deep(.el-tabs__item.is-active) {
  color: #2563eb;
}

.node-tabs :deep(.el-tabs__active-bar) {
  background-color: #2563eb;
  height: 3px;
  border-radius: 3px;
}

.node-status-panel {
  margin-top: 10px;
}

.node-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #edf2f7;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meta-label {
  font-size: 12px;
  color: #8a9bb3;
  font-weight: 500;
}

.meta-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 24px;
}

.uppercase-text {
  text-transform: uppercase;
}

.platform-id-tag {
  background-color: #f1f5f9;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  font-family: ui-monospace, monospace;
}

.status-dot-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 20px;
  height: 22px;
}

.status-dot-badge .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-dot-badge.online {
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.08);
}
.status-dot-badge.online .dot {
  background-color: #10b981;
}

.check-all-box :deep(.el-checkbox__label) {
  font-weight: 600;
  color: #1e293b;
  font-size: 13.5px;
}

.node-list-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.node-list-label {
  padding-top: 4px;
  flex-shrink: 0;
}

.node-list-wrapper {
  background-color: #fbfdff;
  border: 1px dashed #dfe7f3;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}

.node-checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.node-checkbox-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #dfe7f3;
  border-radius: 6px;
  padding: 3px 10px;
  transition: all 0.2s;
}

.node-checkbox-item:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.node-ip-text {
  font-family: ui-monospace, monospace;
  font-size: 12.5px;
  color: #475569;
  font-weight: 600;
}

.node-mini-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-left: 4px;
}
.node-mini-status-dot.success { background-color: #10b981; }
.node-mini-status-dot.error { background-color: #ef4444; }

/* 主工作卡片 */
.table-card {
  background: #ffffff;
  border: 1px solid #dfe7f3;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
  padding: 20px;
  min-height: calc(100vh - 280px);
}

.section-head {
  margin-bottom: 12px;
}
.section-head h3 {
  margin: 0;
  font-size: 17px;
  color: #0f1f3a;
  font-weight: 800;
}
.section-head p {
  margin: 4px 0 0;
  color: #8a9bb3;
  font-size: 12px;
}

.premium-tabs :deep(.el-tabs__item) {
  font-size: 14.5px;
  font-weight: 600;
  color: #64748b;
  height: 38px;
  line-height: 38px;
}
.premium-tabs :deep(.el-tabs__item.is-active) {
  color: #2563eb;
}
.premium-tabs :deep(.el-tabs__active-bar) {
  background-color: #2563eb;
}

/* 过滤工具栏 */
.filter-bar {
  margin-bottom: 16px;
}
.filter-label {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin-right: 8px;
}
.filter-label-text {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}
.filter-select {
  width: 200px;
}
.filter-select :deep(.el-input__inner) {
  border-radius: 8px;
  border-color: #dfe7f3;
  height: 34px;
}

/* ==========================================================================
   全新动作 Hero 卡片平铺网格设计
   ========================================================================== */
.action-hero-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 14px;
  margin-bottom: 22px;
}

.action-hero-card {
  background-color: #ffffff;
  border: 1px solid #dfe7f3;
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.01);
}

.action-hero-card:hover {
  transform: translateY(-2px);
  border-color: #2563eb;
  box-shadow: 0 8px 16px rgba(37, 99, 235, 0.06);
}

.action-hero-card.disabled {
  opacity: 0.45;
  pointer-events: none;
  background-color: #f8fafc;
}

.hero-icon-box {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

/* 动作卡片多色主题 */
.action-hero-card.get .hero-icon-box { background-color: rgba(37, 99, 235, 0.08); color: #2563eb; }
.action-hero-card.post .hero-icon-box { background-color: rgba(16, 185, 129, 0.08); color: #10b981; }
.action-hero-card.put .hero-icon-box { background-color: rgba(245, 158, 11, 0.08); color: #f59e0b; }
.action-hero-card.delete .hero-icon-box { background-color: rgba(239, 68, 68, 0.08); color: #ef4444; }
.action-hero-card.visitor_pull .hero-icon-box { background-color: rgba(147, 51, 234, 0.08); color: #9333ea; }
.action-hero-card.nacos-sync-action .hero-icon-box { background-color: rgba(37, 99, 235, 0.08); color: #2563eb; }

.action-hero-card:hover.get,
.action-hero-card:hover.nacos-sync-action { border-color: #2563eb; }
.action-hero-card:hover.post { border-color: #10b981; }
.action-hero-card:hover.put { border-color: #f59e0b; }
.action-hero-card:hover.delete { border-color: #ef4444; }

.hero-card-body {
  flex: 1;
  min-width: 0;
}
.hero-title {
  margin: 0 0 3px 0;
  font-size: 13.5px;
  font-weight: 700;
  color: #1e293b;
}
.hero-desc {
  margin: 0;
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hero-arrow {
  color: #94a3b8;
  font-size: 14px;
  transition: all 0.2s ease;
}
.action-hero-card:hover .hero-arrow {
  transform: translateX(3px);
  color: inherit;
}

/* ==========================================================================
   优雅的空/等待操作指引看板
   ========================================================================== */
.waiting-guide-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 44px 20px;
  background-color: #f8fafc;
  border: 1px dashed #dfe7f3;
  border-radius: 12px;
  text-align: center;
  margin-top: 10px;
  min-height: 280px;
}

.guide-illustration {
  width: 52px;
  height: 52px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  color: #2563eb;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  position: relative;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.guide-pulse-ring {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px solid rgba(37, 99, 235, 0.3);
  border-radius: 14px;
  animation: pulse-ring 2s cubic-bezier(0.215, 0.610, 0.355, 1) infinite;
}

@keyframes pulse-ring {
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 0; }
  100% { transform: scale(1.15); opacity: 0; }
}

.guide-title {
  margin: 16px 0 6px 0;
  font-size: 15.5px;
  font-weight: 800;
  color: #0f172a;
}
.guide-desc {
  margin: 0;
  font-size: 12.5px;
  color: #64748b;
  max-width: 480px;
  line-height: 1.5;
}

.guide-status-dashboard {
  display: flex;
  gap: 40px;
  margin-top: 24px;
  padding: 12px 28px;
  background: #ffffff;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.02);
}

.dashboard-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.stat-label {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
}
.stat-value {
  font-size: 12.5px;
  font-weight: 700;
  color: #1e293b;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.stat-value.time {
  font-family: ui-monospace, monospace;
  font-weight: 600;
  color: #475569;
}
.stat-value .status-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: #f59e0b;
}
.stat-value.success .status-dot { background-color: #10b981; }
.stat-value.error .status-dot { background-color: #ef4444; }
.stat-value.pending .status-dot { background-color: #f59e0b; }

/* ==========================================================================
   美化的参数同步结果与页签切换区
   ========================================================================== */
.result-panel {
  margin-top: 10px;
}

.result-panel-header-new {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f1f5f9;
}

.result-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
}
.result-section-title .title-text {
  font-size: 14.5px;
  font-weight: 800;
  color: #0f172a;
}
.result-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
}
.result-badge.success { color: #10b981; background-color: rgba(16, 185, 129, 0.08); }
.result-badge.error { color: #ef4444; background-color: rgba(239, 68, 68, 0.08); }

.result-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.result-node-tabs-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.result-node-tab-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}
.result-node-tab-item:hover {
  background-color: #f1f5f9;
}
.result-node-tab-item.active {
  background-color: rgba(37, 99, 235, 0.06);
  border-color: #2563eb;
}
.result-node-tab-item .host-ip {
  font-family: ui-monospace, monospace;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
}
.result-node-tab-item.active .host-ip {
  color: #2563eb;
}

.result-node-tab-item .status-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
}
.result-node-tab-item.status-success .status-dot { background-color: #10b981; }
.result-node-tab-item.status-error .status-dot { background-color: #ef4444; }

.result-node-tab-item.status-success.active .status-dot { animation: pulse-green 1.5s infinite; }
.result-node-tab-item.status-error.active .status-dot { animation: pulse-red 1.5s infinite; }

.result-node-tab-item i {
  font-size: 12px;
}
.result-node-tab-item.status-success i { color: #10b981; }
.result-node-tab-item.status-error i { color: #ef4444; }

@keyframes pulse-green {
  0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6); }
  70% { box-shadow: 0 0 0 4px rgba(16, 185, 129, 0); }
  100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
}
@keyframes pulse-red {
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); }
  70% { box-shadow: 0 0 0 4px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
}

.clear-result-btn {
  color: #94a3b8 !important;
  font-weight: 500 !important;
  font-size: 12px !important;
  padding: 0 !important;
}
.clear-result-btn:hover {
  color: #ef4444 !important;
}

/* ==========================================================================
   表格及控制台美化
   ========================================================================== */
.table-wrapper {
  border: 1px solid #edf2f7;
  border-radius: 8px;
  overflow: hidden;
}
.premium-table {
  border: none !important;
}
.premium-table::before { display: none !important; }

.premium-table :deep(.el-table__header-wrapper) th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-weight: 700 !important;
  padding: 10px 0 !important;
  border-bottom: 1px solid #edf2f7 !important;
}
.premium-table :deep(.el-table__row) td {
  padding: 8px 0 !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
.premium-table :deep(.el-table__row:hover) td {
  background-color: rgba(37, 99, 235, 0.01) !important;
}

.param-key-tag {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  color: #1e293b;
  font-weight: 700;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 2.5px 7px;
  border-radius: 5px;
  display: inline-block;
}

.param-value-container {
  max-width: 100%;
  max-height: 80px;
  overflow-y: auto;
  padding: 4px 8px;
  background-color: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}
.param-value-container::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}
.param-value-container::-webkit-scrollbar-thumb {
  background: rgba(37, 99, 235, 0.12);
  border-radius: 2px;
}
.param-value-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: #2563eb;
  font-weight: 600;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
}

.copy-btn {
  color: #2563eb !important;
  font-weight: 600 !important;
  font-size: 12.5px !important;
  padding: 4px 8px !important;
  transition: all 0.2s ease !important;
}
.copy-btn:hover {
  color: #1d4ed8 !important;
  background-color: rgba(37, 99, 235, 0.05) !important;
  border-radius: 6px;
}

/* 控制台输出 (浅色调) */
.terminal-container {
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
  border: 1px solid #dfe7f3;
  overflow: hidden;
  height: 380px;
}
.terminal-header {
  background: #f1f5f9;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #dfe7f3;
}
.terminal-dots {
  display: flex;
  gap: 5px;
}
.terminal-dots .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.terminal-dots .dot.red { background-color: #ef4444; }
.terminal-dots .dot.yellow { background-color: #f59e0b; }
.terminal-dots .dot.green { background-color: #10b981; }

.terminal-title {
  color: #64748b;
  font-size: 11.5px;
  font-family: ui-monospace, monospace;
  font-weight: 600;
}
.terminal-body {
  padding: 14px;
  flex: 1;
  overflow-y: auto;
}
.terminal-host-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.terminal-host-status {
  width: 5px;
  height: 5px;
  border-radius: 50%;
}
.terminal-host-status.success { background-color: #10b981; }
.terminal-host-status.error { background-color: #ef4444; }

.terminal-host-name {
  color: #1e293b;
  font-size: 12px;
  font-family: ui-monospace, monospace;
  font-weight: 700;
}
.terminal-pre {
  background: #ffffff;
  color: #2563eb;
  padding: 12px;
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.5;
  margin: 0;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
}
.terminal-pre.err { color: #ef4444; }

/* ==========================================================================
   可配置的 Nacos 配置网格样式
   ========================================================================== */
.nacos-config-panel-new {
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.nacos-card {
  padding: 20px 22px;
}

.nacos-panel {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.nacos-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.nacos-panel-header .card-title {
  margin: 0;
  color: #0f172a;
  font-size: 16px;
  line-height: 26px;
  font-weight: 700;
}

.nacos-action-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}
.nacos-bar-btn {
  border-radius: 6px !important;
  font-weight: 600 !important;
}
.nacos-selected-tip {
  margin-top: 2px;
  font-size: 12.5px;
  color: #64748b;
}
.nacos-selected-tip .highlight-text {
  color: #2563eb;
  font-size: 13.5px;
}
.nacos-grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 14px;
}
.nacos-config-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.03);
  user-select: none;
}
.nacos-config-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.06);
  border-color: #cbd5e1;
}
.nacos-config-card.checked {
  border-color: #2563eb;
  background-color: #eff6ff;
  box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.35);
}
.card-checkbox-wrapper {
  position: absolute;
  top: 10px;
  right: 10px;
  pointer-events: none;
}
.card-main-content {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.card-icon-box {
  width: 32px;
  height: 32px;
  background-color: #f1f5f9;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 16px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}
.nacos-config-card.checked .card-icon-box {
  background-color: rgba(37, 99, 235, 0.1);
  color: #2563eb;
}
.card-text {
  flex: 1;
  min-width: 0;
  padding-right: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.card-title-text {
  margin: 0 0 3px 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-desc {
  margin: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-status-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
  font-size: 11px;
}
.items-count {
  color: #94a3b8;
  font-weight: 500;
}
.sync-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
}
.nacos-status-pending .sync-badge { color: #94a3b8; }
.nacos-status-syncing { border-color: #f59e0b; }
.nacos-status-syncing .sync-badge { color: #f59e0b; }
.nacos-status-success { border-color: #10b981; }
.nacos-status-success.checked { box-shadow: 0 0 0 1px #10b981; }
.nacos-status-success .sync-badge { color: #10b981; }
.nacos-status-error { border-color: #ef4444; }
.nacos-status-error.checked { box-shadow: 0 0 0 1px #ef4444; }
.nacos-status-error .sync-badge { color: #ef4444; }

.nacos-bar-btn.el-button--primary.is-plain {
  background-color: rgba(37, 99, 235, 0.04) !important;
  border-color: rgba(37, 99, 235, 0.25) !important;
  color: #2563eb !important;
}
.nacos-bar-btn.el-button--primary.is-plain:hover {
  background-color: #2563eb !important;
  border-color: #2563eb !important;
  color: #ffffff !important;
}

.nacos-bar-btn.el-button--info.is-plain {
  background-color: #f8fafc !important;
  border-color: #cbd5e1 !important;
  color: #64748b !important;
}
.nacos-bar-btn.el-button--info.is-plain:hover {
  background-color: #64748b !important;
  border-color: #64748b !important;
  color: #ffffff !important;
}

/* ==========================================================================
   同步参数与重载NACOS页面暗色模式适配覆盖
   ========================================================================== */
html.dark .sync-workspace {
  background-color: var(--bg-page);
  color: var(--text-main);
}
html.dark .sync-card,
html.dark .node-card {
  background-color: var(--bg-card);
  border-color: var(--border-color);
}
html.dark .sync-card > .card-title,
html.dark .result-card-header > .card-title,
html.dark .page-heading h1 {
  color: var(--text-main);
}
html.dark .group-segment {
  border-color: var(--border-color);
  background-color: var(--bg-page);
}
html.dark .segment-item {
  background-color: var(--bg-card);
  border-color: var(--border-color);
  color: var(--text-muted);
}
html.dark .segment-item.active {
  color: var(--primary-color);
  background-color: var(--bg-tip-hover);
  box-shadow: inset 0 0 0 1px var(--primary-color);
}
html.dark .module-tabs :deep(.el-tabs__nav-wrap::after) {
  background-color: var(--border-color);
}
html.dark .module-tabs :deep(.el-tabs__item) {
  color: var(--text-muted);
}
html.dark .module-tabs :deep(.el-tabs__item:hover) {
  color: var(--primary-color);
  background-color: var(--bg-hover);
}
html.dark .module-tabs :deep(.el-tabs__item.is-active) {
  color: var(--primary-color);
  background-color: var(--bg-tip-hover);
}
html.dark .readonly-selector {
  color: var(--text-main);
  background-color: var(--bg-page);
  border-color: var(--border-color);
}
html.dark .nacos-result-list,
html.dark .node-panel,
html.dark .server-panel {
  border-color: var(--border-color) !important;
  background-color: var(--bg-card) !important;
}
html.dark .nacos-result-row {
  border-bottom-color: var(--border-color) !important;
}
html.dark .nacos-result-name {
  color: var(--text-main) !important;
}
html.dark .nacos-result-row code {
  color: var(--text-muted) !important;
  background-color: var(--bg-page) !important;
  border-color: var(--border-color) !important;
}
html.dark .panel-subhead,
html.dark .result-toolbar {
  border-bottom-color: var(--border-color) !important;
  color: var(--text-main) !important;
  background-color: var(--bg-card) !important;
}
html.dark .node-table-head {
  color: var(--text-muted) !important;
  background-color: var(--bg-page) !important;
  border-bottom-color: var(--border-color) !important;
}
html.dark .node-row {
  border-bottom-color: var(--border-color) !important;
}
html.dark .node-row:hover {
  background-color: var(--bg-hover) !important;
}
html.dark .node-row.active {
  background-color: var(--bg-hover) !important;
  box-shadow: inset 3px 0 0 var(--primary-color) !important;
}
html.dark .node-host {
  color: var(--primary-color) !important;
}
html.dark .code-shell,
html.dark .json-code {
  background-color: #0f172a !important; /* 代码shell在暗色模式下使用深黑底色，非常高级 */
  color: #fb7185 !important;
}
html.dark .empty-result i {
  color: var(--border-hover) !important;
}

/* Nacos配置卡片暗色 */
html.dark .nacos-config-card {
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
}
html.dark .nacos-config-card:hover {
  border-color: var(--border-hover) !important;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4) !important;
}
html.dark .nacos-config-card.checked {
  border-color: var(--primary-color) !important;
  background-color: rgba(59, 130, 246, 0.12) !important;
  box-shadow: 0 0 0 1px var(--primary-color) !important;
}
html.dark .card-icon-box {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
}
html.dark .nacos-config-card.checked .card-icon-box {
  background-color: rgba(59, 130, 246, 0.2) !important;
  color: #60a5fa !important;
}
html.dark .card-title-text {
  color: var(--text-main) !important;
}
html.dark .card-desc {
  color: var(--text-muted) !important;
}
html.dark .card-status-footer {
  border-top-color: var(--border-color) !important;
}
html.dark .items-count {
  color: var(--text-muted) !important;
}
html.dark .nacos-status-pending .sync-badge { color: var(--text-muted) !important; }

/* status-pill 适配 */
html.dark .status-pill.pending {
  color: var(--text-muted) !important;
  background-color: var(--bg-hover) !important;
}
html.dark .status-pill.syncing {
  color: #fbbf24 !important;
  background-color: rgba(245, 158, 11, 0.15) !important;
}
html.dark .status-pill.success {
  color: #34d399 !important;
  background-color: rgba(16, 185, 129, 0.15) !important;
  border-color: rgba(16, 185, 129, 0.3) !important;
}
html.dark .status-pill.error {
  color: #f87171 !important;
  background-color: rgba(239, 68, 68, 0.15) !important;
  border-color: rgba(239, 68, 68, 0.3) !important;
}

/* 适配指引卡片 */
html.dark .waiting-guide-panel {
  background-color: var(--bg-hover) !important;
  border-color: var(--border-color) !important;
}
html.dark .guide-illustration {
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  color: var(--primary-color) !important;
  box-shadow: none !important;
}
html.dark .waiting-guide-panel .hero-title {
  color: var(--text-main) !important;
}
html.dark .waiting-guide-panel .hero-desc {
  color: var(--text-muted) !important;
}

/* 适配节点页签切换项 */
html.dark .result-node-tab-item {
  background-color: var(--bg-hover) !important;
  border-color: var(--border-color) !important;
}
html.dark .result-node-tab-item:hover {
  background-color: var(--bg-hover-deep) !important;
}
html.dark .result-node-tab-item.active {
  background-color: rgba(37, 99, 235, 0.15) !important;
  border-color: var(--primary-color) !important;
}
html.dark .result-node-tab-item .host-ip {
  color: var(--text-muted) !important;
}
html.dark .result-node-tab-item.active .host-ip {
  color: var(--primary-color) !important;
}

/* 头部及下划线边框适配 */
html.dark .result-panel-header-new {
  border-bottom-color: var(--border-color) !important;
}
html.dark .result-section-title .title-text {
  color: var(--text-main) !important;
}

/* NACOS 重载页面深色模式精致高质感美化 */
html.dark .nacos-panel-header {
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}

html.dark .nacos-panel-header .card-title,
html.dark .result-card-header .card-title {
  color: #f8fafc !important;
  font-size: 16px !important;
  font-weight: 700 !important;
}

html.dark .nacos-selected-tip {
  color: #94a3b8 !important;
}

html.dark .nacos-selected-tip .highlight-text {
  color: #60a5fa !important;
}

/* 配置卡片深色呈现 */
html.dark .nacos-config-card {
  background-color: rgba(30, 41, 59, 0.75) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25) !important;
}

html.dark .nacos-config-card:hover {
  background-color: rgba(30, 41, 59, 0.95) !important;
  border-color: rgba(96, 165, 250, 0.45) !important;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4) !important;
}

html.dark .nacos-config-card.checked {
  background-color: rgba(37, 99, 235, 0.18) !important;
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 1px #3b82f6, 0 4px 16px rgba(37, 99, 235, 0.3) !important;
}

html.dark .card-icon-box {
  background-color: rgba(255, 255, 255, 0.07) !important;
  color: #94a3b8 !important;
}

html.dark .nacos-config-card.checked .card-icon-box {
  background-color: rgba(59, 130, 246, 0.25) !important;
  color: #60a5fa !important;
}

html.dark .card-title-text {
  color: #f8fafc !important;
}

html.dark .card-desc {
  color: #94a3b8 !important;
}

html.dark .card-status-footer {
  border-top-color: rgba(255, 255, 255, 0.08) !important;
}

html.dark .items-count {
  color: #94a3b8 !important;
}

html.dark .nacos-status-pending .sync-badge {
  color: #94a3b8 !important;
}

/* 按钮深色适配 */
html.dark .nacos-action-bar .el-button--plain,
html.dark .result-card-header .el-button--plain {
  background-color: rgba(255, 255, 255, 0.06) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  color: #e2e8f0 !important;
}

html.dark .nacos-action-bar .el-button--plain:hover,
html.dark .result-card-header .el-button--plain:hover {
  background-color: rgba(59, 130, 246, 0.15) !important;
  border-color: #3b82f6 !important;
  color: #60a5fa !important;
}

html.dark .nacos-action-bar .el-button--success {
  background-color: #059669 !important;
  border-color: #059669 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25) !important;
}

html.dark .nacos-action-bar .el-button--success:hover {
  background-color: #10b981 !important;
  border-color: #10b981 !important;
}

html.dark .nacos-action-bar .el-button--success.is-disabled {
  background-color: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: #64748b !important;
  box-shadow: none !important;
}

/* 重载结果深色面板 */
html.dark .nacos-result-list .empty-state {
  color: #94a3b8 !important;
}

html.dark .nacos-result-row {
  background-color: rgba(30, 41, 59, 0.6) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

html.dark .nacos-result-name {
  color: #f8fafc !important;
}

html.dark .nacos-result-row code {
  color: #93c5fd !important;
  background-color: rgba(15, 23, 42, 0.8) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

html.dark .action-hero-card {
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
}

html.dark .action-hero-card:hover {
  border-color: var(--primary-color) !important;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4) !important;
}

html.dark .hero-title {
  color: var(--text-main) !important;
}

html.dark .hero-desc {
  color: var(--text-muted) !important;
}
</style>
